/**
 * Sites API endpoints for site management.
 */

import { api, apiRequestBinary } from './client';
import type { BundledTheme, ThemeFileContent } from './themes';
import type {
  Site,
  DetachResponse,
  SiteSettings,
  ApiResponse,
  CreateSiteRequest,
  UpdateSiteRequest,
  MigrateDbRequest,
  MigrateDbResponse,
  MigrationPreflightRequest,
  MigrationPreflight,
  MigrationStatusResponse,
  MigrationCancelResponse,
  UpdateSiteSettingsRequest,
  BuildResponse,
  PreviewShare,
  PreviewShareCreate,
  SiteDeployState,
} from '../types';

/** Sites API */
export const sitesApi = {
  /** List all sites */
  list: () => api.get<ApiResponse<Site[]>>('/v1/sites'),

  /** Get a site by slug */
  get: (slug: string) => api.get<ApiResponse<Site>>(`/v1/sites/${slug}`),

  /** AB#820: saved-but-unpublished changes since the last publish. */
  deployState: (slug: string) =>
    api.get<ApiResponse<SiteDeployState>>(`/v1/sites/${encodeURIComponent(slug)}/deploy-state`),

  /** Create a new site */
  create: (data: CreateSiteRequest) => api.post<ApiResponse<Site>>('/v1/sites', data),

  /** Update a site */
  update: (slug: string, data: UpdateSiteRequest) =>
    api.put<ApiResponse<Site>>(`/v1/sites/${slug}`, data),

  /** Delete a site */
  delete: (slug: string) => api.delete<ApiResponse<null>>(`/v1/sites/${slug}`),

  /** Migrate site database to a new database type (async - returns job_id) */
  migrationPreflight: (slug: string, data: MigrationPreflightRequest) =>
    api.post<ApiResponse<MigrationPreflight>>(`/v1/sites/${encodeURIComponent(slug)}/migration-preflight`, data),

  migrate: (slug: string, data: MigrateDbRequest) =>
    api.post<ApiResponse<MigrateDbResponse>>(`/v1/sites/${slug}/migrate`, data),

  /** Get migration job status */
  getMigrationStatus: (slug: string) =>
    api.get<ApiResponse<MigrationStatusResponse>>(`/v1/sites/${slug}/migration-status`),

  /** Cancel in-progress migration */
  cancelMigration: (slug: string, expectedJobId: number) =>
    api.post<ApiResponse<MigrationCancelResponse>>(`/v1/sites/${slug}/migration-cancel`, { expected_job_id: expectedJobId }),

  /** Get site settings */
  getSettings: (slug: string) =>
    api.get<ApiResponse<SiteSettings>>(`/v1/sites/${slug}/settings`),

  /** Update site settings */
  updateSettings: (slug: string, data: UpdateSiteSettingsRequest) =>
    api.put<ApiResponse<SiteSettings>>(`/v1/sites/${slug}/settings`, data),

  /** Trigger SSG build */
  build: (slug: string, body?: { preview_theme_id?: number; preview_theme?: string }) =>
    api.post<ApiResponse<BuildResponse>>(`/v1/sites/${slug}/build`, body ?? {}),

  /** List available themes */
  /** `files` arrives only when asked for (AB#644) — the admin's stock tiles want it. */
  listThemes: (withFiles = false) =>
    api.get<ApiResponse<BundledTheme[]>>(`/v1/sites/themes${withFiles ? '?files=1' : ''}`),

  /**
   * Read one file out of a BUNDLED theme (AB#644). Read-only: stock themes ship
   * in the image, so there is no write or delete twin.
   */
  bundledThemeFile: (id: string, path: string) =>
    api.get<ApiResponse<ThemeFileContent>>(
      `/v1/sites/themes/${encodeURIComponent(id)}/files/${path
        .split('/')
        .map(encodeURIComponent)
        .join('/')}`,
    ),

  /** Get download URL for build output */
  getDownloadUrl: (slug: string) => `/api/v1/sites/${slug}/download`,

  /**
   * AB#661: one page out of the site's last build, standalone (theme CSS/fonts
   * inlined) so it renders in an `<iframe srcdoc>`.
   *
   * @param path Site-relative URL path, e.g. `/`, `/404.html`, `/member/signup/`.
   */
  builtPage: (slug: string, path: string, previewThemeId?: number, previewTheme?: string) =>
    api.get<ApiResponse<{ html: string; artifact_id?: string }>>(
      `/v1/sites/${slug}/built?path=${encodeURIComponent(path)}${
        previewThemeId != null ? `&preview_theme_id=${previewThemeId}` : ''
      }${previewTheme ? `&preview_theme=${encodeURIComponent(previewTheme)}` : ''}`,
    ),

  /** Snapshot an already-built selected theme with navigable pages and assets. */
  mintBuiltPreview: (slug: string, body: { preview_theme_id?: number; preview_theme?: string; path?: string }) =>
    api.post<ApiResponse<{ key: string; url: string; expires_in: number }>>(
      `/v1/sites/${encodeURIComponent(slug)}/built-preview`, body,
    ),

  /** AB#673: mint a new preview key — every existing owner link dies. */
  rotatePreviewKey: (slug: string) =>
    api.post<ApiResponse<{ preview_url: string }>>(`/v1/sites/${slug}/preview-key/rotate`, {}),

  /** AB#673: shareable preview links (public or per-email). */
  listPreviewShares: (slug: string) =>
    api.get<ApiResponse<PreviewShare[]>>(`/v1/sites/${slug}/preview-shares`),

  createPreviewShare: (slug: string, body: PreviewShareCreate) =>
    api.post<ApiResponse<PreviewShare>>(`/v1/sites/${slug}/preview-shares`, body),

  /** Mail the link (email shares only). */
  sendPreviewShare: (slug: string, token: string) =>
    api.post<ApiResponse<Record<string, never>>>(
      `/v1/sites/${slug}/preview-shares/${encodeURIComponent(token)}/send`,
      {},
    ),

  revokePreviewShare: (slug: string, token: string) =>
    api.delete<ApiResponse<null>>(`/v1/sites/${slug}/preview-shares/${encodeURIComponent(token)}`),

  /** Move site content into a server-side SQLite export and disable hosting. */
  detach: (slug: string, acknowledgements?: { maintenance_acknowledged: boolean; external_writers_quiesced: boolean; ack_hosting_disabled: boolean; retire_live_services?: boolean; typed_site_slug?: string; reviewed_fingerprint?: string }) => api.post<ApiResponse<DetachResponse>>(`/v1/sites/${slug}/detach`, acknowledgements ?? {}),

  /** Download the detached-content sqlite export (bytes). */
  downloadDetachedDb: (slug: string) => apiRequestBinary(`/v1/sites/${slug}/detach/download`),
};
